import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Store, CheckCircle2 } from 'lucide-react';
import Logo from '../components/Logo';

const CATEGORIES = [
  'Ristorazione', 'Servizi', 'Shopping', 'Salute e Benessere',
  'Casa e Giardino', 'Tempo Libero', 'Auto e Moto'
];

const RegisterCompany = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    referent_name: '',
    company_name: '',
    vat_number: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    category: '',
    description: '',
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ['referent_name', 'company_name', 'vat_number', 'email', 'phone'];
    for (const f of required) {
      if (!form[f] || String(form[f]).trim() === '') {
        toast.error('Compila tutti i campi obbligatori');
        return;
      }
    }
    setLoading(true);
    try {
      await api.post('/public/company-requests', form);
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante l\'invio della richiesta');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-slate-200 shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Richiesta inviata!</CardTitle>
            <CardDescription>
              La tua richiesta di registrazione è stata ricevuta. L'amministratore di AIAC Card la valuterà
              e ti contatteremo via email con l'esito.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/')} data-testid="back-home-btn">
              Torna alla Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla home
          </Link>
        </div>

        <Card className="border-slate-200 shadow-xl">
          <CardHeader className="text-center pb-6">
            <div className="mx-auto mb-4 flex items-center justify-center">
              <Logo linkTo={null} size="lg" />
            </div>
            <CardTitle className="text-2xl font-serif">Diventa Partner AIAC Card</CardTitle>
            <CardDescription>
              Compila il form per richiedere l'iscrizione della tua attività. Un amministratore
              esaminerà la richiesta e ti contatterà con le credenziali di accesso.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="referent_name">Nome e Cognome Referente *</Label>
                  <Input id="referent_name" value={form.referent_name}
                    onChange={(e) => update('referent_name', e.target.value)}
                    placeholder="Mario Rossi"
                    required data-testid="rc-referent-input" />
                </div>
                <div>
                  <Label htmlFor="company_name">Ragione Sociale *</Label>
                  <Input id="company_name" value={form.company_name}
                    onChange={(e) => update('company_name', e.target.value)}
                    placeholder="Bar Centrale SRL"
                    required data-testid="rc-company-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="vat_number">P.IVA *</Label>
                  <Input id="vat_number" value={form.vat_number}
                    onChange={(e) => update('vat_number', e.target.value.toUpperCase())}
                    placeholder="IT12345678901"
                    required data-testid="rc-vat-input" />
                </div>
                <div>
                  <Label htmlFor="phone">Telefono *</Label>
                  <Input id="phone" type="tel" value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    placeholder="+39 333 1234567"
                    required data-testid="rc-phone-input" />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="info@bar-centrale.it"
                  required data-testid="rc-email-input" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">Città</Label>
                  <Input id="city" value={form.city}
                    onChange={(e) => update('city', e.target.value)}
                    placeholder="Milano" data-testid="rc-city-input" />
                </div>
                <div>
                  <Label>Categoria</Label>
                  <Select value={form.category} onValueChange={(v) => update('category', v)}>
                    <SelectTrigger data-testid="rc-category-select">
                      <SelectValue placeholder="Seleziona categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="address">Indirizzo</Label>
                <Input id="address" value={form.address}
                  onChange={(e) => update('address', e.target.value)}
                  placeholder="Via Roma 10" data-testid="rc-address-input" />
              </div>

              <div>
                <Label htmlFor="description">Descrizione attività</Label>
                <Textarea id="description" value={form.description}
                  onChange={(e) => update('description', e.target.value)}
                  placeholder="Raccontaci della tua attività..."
                  data-testid="rc-description-input" />
              </div>

              <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600"
                disabled={loading} data-testid="rc-submit-btn">
                {loading ? 'Invio in corso...' : 'Invia richiesta'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterCompany;
